import React, { useEffect, useMemo, useState } from 'react';
import { MessageSquare, Filter, Edit3, Save, X, LayoutDashboard, Check, CheckSquare, Trash2 } from 'lucide-react';
import IconButton from './IconButton';

const STATUS_COLUMNS = ['Backlog', 'Planejamento', 'Em andamento', 'Concluído'];
const PRIORIDADES = ['Baixa', 'Normal', 'Alta', 'Urgente'];

function formatDate(value) {
  if (!value) return '—';
  // Tenta formato ISO primeiro
  try {
    const s = String(value);
    const iso10 = s.substring(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso10)) {
      const [y, m, d] = iso10.split('-');
      return `${d}/${m}/${y}`;
    }
  } catch {}
  // Fallback via Date, mas formatando manualmente dd/mm/aaaa
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return '—';
  }
}

const DemandasGrid = ({ items, loading, onChangeStatus, onUpdateQuick, users = [], funcionarios = [] }) => {
  const [sortKey, setSortKey] = useState('id_dem');
  const [sortDir, setSortDir] = useState('desc');
  const [editing, setEditing] = useState({}); // { [id_dem]: draft }
  const [allEdit, setAllEdit] = useState(false);
  const [dashboardView, setDashboardView] = useState(false);
  const [obsModalItem, setObsModalItem] = useState(null);
  const [editModalItem, setEditModalItem] = useState(null);
  const [editModalDraft, setEditModalDraft] = useState(null);

  // Filtro de Status (cabeçalho)
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const [statusFilterAnchor, setStatusFilterAnchor] = useState(null); // { top, left }
  const [statusFilterSelected, setStatusFilterSelected] = useState(null); // null = todos disponíveis
  const [statusFilterSearch, setStatusFilterSearch] = useState('');
  const [statusFilterTempSelected, setStatusFilterTempSelected] = useState([]);

  // Filtro de Prioridade (cabeçalho)
  const [prioFilterOpen, setPrioFilterOpen] = useState(false);
  const [prioFilterAnchor, setPrioFilterAnchor] = useState(null); // { top, left }
  const [prioFilterSelected, setPrioFilterSelected] = useState(null); // null = todas disponíveis
  const [prioFilterSearch, setPrioFilterSearch] = useState('');
  const [prioFilterTempSelected, setPrioFilterTempSelected] = useState([]);

  // Filtro de Responsável (cabeçalho)
  const [respFilterOpen, setRespFilterOpen] = useState(false);
  const [respFilterAnchor, setRespFilterAnchor] = useState(null); // { top, left }
  const [respFilterSelected, setRespFilterSelected] = useState(null); // null = todos disponíveis
  const [respFilterSearch, setRespFilterSearch] = useState('');
  const [respFilterTempSelected, setRespFilterTempSelected] = useState([]);

  // Filtro de Solicitante (cabeçalho)
  const [solFilterOpen, setSolFilterOpen] = useState(false);
  const [solFilterAnchor, setSolFilterAnchor] = useState(null); // { top, left }
  const [solFilterSelected, setSolFilterSelected] = useState(null); // null = todos disponíveis
  const [solFilterSearch, setSolFilterSearch] = useState('');
  const [solFilterTempSelected, setSolFilterTempSelected] = useState([]);

  const getPersonName = (idOrName) => {
    if (!idOrName) return '—';
    const u = users.find(u => String(u.id) === String(idOrName));
    if (u) return u.name;
    const f = funcionarios.find(f => String(f.id_func) === String(idOrName));
    return f ? f.nome_func : String(idOrName);
  };

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Indica se um filtro está ativo (diferente do padrão "todos marcados")
  const isActiveFilter = (selected, available) => {
    if (selected === null) return false; // padrão
    if (!Array.isArray(selected)) return false;
    if (!Array.isArray(available) || available.length === 0) return selected.length > 0;
    if (selected.length !== available.length) return true;
    const setSel = new Set(selected);
    for (const v of available) {
      if (!setSel.has(v)) return true;
    }
    return false; // igual ao conjunto completo
  };

  const isCompleted = (item) => String(item.status) === 'Concluído';
  const getRowClass = (item) => {
    const s = String(item.status || '');
    if (s === 'Concluído') return 'bg-green-50 font-semibold';
    if (s === 'Em andamento') return 'bg-amber-50';
    if (s === 'Planejamento') return 'bg-blue-50';
    return '';
  };

  // Valores de status disponíveis a partir dos itens
  const availableStatuses = useMemo(() => {
    const set = new Set();
    (items || []).forEach(it => {
      const val = it?.status;
      if (val === null || val === undefined || String(val).trim() === '') {
        set.add('');
      } else {
        set.add(String(val));
      }
    });
    // Garante ordem previsível: usa STATUS_COLUMNS como guia, depois quaisquer extras
    const extras = Array.from(set).filter(s => !STATUS_COLUMNS.includes(s));
    const ordered = [
      ...STATUS_COLUMNS.filter(s => set.has(s)),
      ...extras
    ];
    // Se existir vazio, coloca como primeiro
    if (set.has('') && !ordered.includes('')) ordered.unshift('');
    return ordered;
  }, [items]);

  // Valores de prioridades disponíveis a partir dos itens
  const availablePriorities = useMemo(() => {
    const set = new Set();
    (items || []).forEach(it => {
      const val = it?.pri_dem;
      if (val === null || val === undefined || String(val).trim() === '') {
        set.add('');
      } else {
        set.add(String(val));
      }
    });
    const extras = Array.from(set).filter(p => !PRIORIDADES.includes(p));
    const ordered = [
      ...PRIORIDADES.filter(p => set.has(p)),
      ...extras
    ];
    if (set.has('') && !ordered.includes('')) ordered.unshift('');
    return ordered;
  }, [items]);

  // Responsáveis disponíveis a partir dos itens (resolve nomes)
  const availableResponsibles = useMemo(() => {
    const set = new Set();
    (items || []).forEach(it => {
      const val = it?.respon_dem;
      const label = getPersonName(val);
      const isEmpty = val === null || val === undefined || String(val).trim() === '';
      if (isEmpty) {
        set.add('');
      } else {
        set.add(String(label));
      }
    });
    const nonEmpty = Array.from(set).filter(v => v !== '');
    nonEmpty.sort((a, b) => String(a).localeCompare(String(b), 'pt-BR', { sensitivity: 'base' }));
    if (set.has('')) nonEmpty.unshift('');
    return nonEmpty;
  }, [items, users, funcionarios]);

  // Solicitantes disponíveis a partir dos itens (resolve nomes)
  const availableRequesters = useMemo(() => {
    const set = new Set();
    (items || []).forEach(it => {
      const val = it?.solic_dem;
      const label = getPersonName(val);
      const isEmpty = val === null || val === undefined || String(val).trim() === '';
      if (isEmpty) {
        set.add('');
      } else {
        set.add(String(label));
      }
    });
    const nonEmpty = Array.from(set).filter(v => v !== '');
    nonEmpty.sort((a, b) => String(a).localeCompare(String(b), 'pt-BR', { sensitivity: 'base' }));
    if (set.has('')) nonEmpty.unshift('');
    return nonEmpty;
  }, [items, users, funcionarios]);

  // Itens filtrados por Status e Prioridade
  const filteredItems = useMemo(() => {
    const activeStatus = statusFilterSelected === null ? availableStatuses : statusFilterSelected;
    const activePrio = prioFilterSelected === null ? availablePriorities : prioFilterSelected;
    const activeResp = respFilterSelected === null ? availableResponsibles : respFilterSelected;
    const activeSol = solFilterSelected === null ? availableRequesters : solFilterSelected;
    if (!activeStatus || activeStatus.length === 0) return [];
    if (!activePrio || activePrio.length === 0) return [];
    if (!activeResp || activeResp.length === 0) return [];
    if (!activeSol || activeSol.length === 0) return [];
    return (items || []).filter(it => {
      const stVal = it?.status;
      const stNorm = (stVal === null || stVal === undefined || String(stVal).trim() === '') ? '' : String(stVal);
      const prVal = it?.pri_dem;
      const prNorm = (prVal === null || prVal === undefined || String(prVal).trim() === '') ? '' : String(prVal);
      const respVal = it?.respon_dem;
      const respIsEmpty = respVal === null || respVal === undefined || String(respVal).trim() === '';
      const respLabel = respIsEmpty ? '' : String(getPersonName(respVal));
      const solVal = it?.solic_dem;
      const solIsEmpty = solVal === null || solVal === undefined || String(solVal).trim() === '';
      const solLabel = solIsEmpty ? '' : String(getPersonName(solVal));
      return activeStatus.includes(stNorm) && activePrio.includes(prNorm) && activeResp.includes(respLabel) && activeSol.includes(solLabel);
    });
  }, [items, availableStatuses, statusFilterSelected, availablePriorities, prioFilterSelected, availableResponsibles, respFilterSelected, availableRequesters, solFilterSelected]);

  const sorted = useMemo(() => {
    const arr = Array.isArray(filteredItems) ? [...filteredItems] : [];
    arr.sort((a, b) => {
      const va = a?.[sortKey];
      const vb = b?.[sortKey];
      // Prefer numeric comparison when both sides look numeric
      const vaNum = Number(va);
      const vbNum = Number(vb);
      const bothNumeric = !Number.isNaN(vaNum) && !Number.isNaN(vbNum);
      if (bothNumeric) {
        if (vaNum < vbNum) return sortDir === 'asc' ? -1 : 1;
        if (vaNum > vbNum) return sortDir === 'asc' ? 1 : -1;
        return 0;
      }
      const na = va === null || va === undefined ? '' : String(va).toLowerCase();
      const nb = vb === null || vb === undefined ? '' : String(vb).toLowerCase();
      if (na < nb) return sortDir === 'asc' ? -1 : 1;
      if (na > nb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredItems, sortKey, sortDir]);

  // Itens para a visão de dashboard: somente demandas com status diferente de "Concluído"
  const dashboardItems = useMemo(() => {
    return sorted.filter(i => String(i.status) !== 'Concluído');
  }, [sorted]);

  // Permite fechar o modo dashboard com a tecla Esc
  useEffect(() => {
    if (!dashboardView) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setDashboardView(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dashboardView]);

  const startEdit = (item) => {
    if (isCompleted(item)) return; // bloqueia edição se concluído
    setEditing(ed => ({
      ...ed,
      [item.id_dem]: {
        descr_dem: item.descr_dem || '',
        respon_dem: item.respon_dem || '',
        solic_dem: item.solic_dem || '',
        dt_cad_dem: item.dt_cad_dem || '',
        dt_cad_ini: item.dt_cad_ini ? String(item.dt_cad_ini).substring(0,10) : '',
        hora_ini_dem: item.hora_ini_dem ? String(item.hora_ini_dem).substring(0,5) : '',
        hora_fin_dem: item.hora_fin_dem ? String(item.hora_fin_dem).substring(0,5) : '',
        dt_prev_dem: item.dt_prev_dem ? String(item.dt_prev_dem).substring(0,10) : '',
        dt_prev_dem_seg: item.dt_prev_dem_seg ? String(item.dt_prev_dem_seg).substring(0,10) : '',
        dt_concl_dem: item.dt_concl_dem || '',
        status: item.status || 'Backlog',
        pri_dem: item.pri_dem || 'Normal',
        obs_dem: item.obs_dem || ''
      }
    }));
  };

  const cancelEdit = (id) => {
    setEditing(ed => {
      const next = { ...ed };
      delete next[id];
      return next;
    });
  };

  const saveEdit = async (item) => {
    const draft = editing[item.id_dem];
    if (!draft) return;
    await onUpdateQuick(item, draft);
    cancelEdit(item.id_dem);
  };

  const ensureAllDrafts = () => {
    setEditing(ed => {
      const next = { ...ed };
      sorted.forEach(item => {
        if (isCompleted(item)) return; // ignora concluídos no modo editar tudo
        if (!next[item.id_dem]) {
          next[item.id_dem] = {
            descr_dem: item.descr_dem || '',
            respon_dem: item.respon_dem || '',
            solic_dem: item.solic_dem || '',
            dt_cad_dem: item.dt_cad_dem || '',
            dt_cad_ini: item.dt_cad_ini ? String(item.dt_cad_ini).substring(0,10) : '',
            hora_ini_dem: item.hora_ini_dem ? String(item.hora_ini_dem).substring(0,5) : '',
            hora_fin_dem: item.hora_fin_dem ? String(item.hora_fin_dem).substring(0,5) : '',
            dt_prev_dem: item.dt_prev_dem ? String(item.dt_prev_dem).substring(0,10) : '',
            dt_prev_dem_seg: item.dt_prev_dem_seg ? String(item.dt_prev_dem_seg).substring(0,10) : '',
            dt_concl_dem: item.dt_concl_dem || '',
            status: item.status || 'Backlog',
            pri_dem: item.pri_dem || 'Normal',
            obs_dem: item.obs_dem || ''
          };
        }
      });
      return next;
    });
  };

  const toggleAllEdit = () => {
    if (!allEdit) {
      ensureAllDrafts();
      setAllEdit(true);
    } else {
      setEditing({});
      setAllEdit(false);
    }
  };

  const saveAll = async () => {
    const ids = Object.keys(editing);
    if (ids.length === 0) return;
    await Promise.all(ids.map(id => {
      const item = sorted.find(i => String(i.id_dem) === String(id));
      if (!item) return Promise.resolve();
      return onUpdateQuick(item, editing[id]);
    }));
    setEditing({});
    setAllEdit(false);
  };

  const cancelAll = () => {
    setEditing({});
    setAllEdit(false);
  };

  const openEditModal = (item) => {
    if (isCompleted(item)) return;
    setEditModalItem(item);
    setEditModalDraft({
      descr_dem: item.descr_dem || '',
      respon_dem: item.respon_dem || '',
      solic_dem: item.solic_dem || '',
      dt_cad_ini: item.dt_cad_ini ? String(item.dt_cad_ini).substring(0,10) : '',
      hora_ini_dem: item.hora_ini_dem ? String(item.hora_ini_dem).substring(0,5) : '',
      hora_fin_dem: item.hora_fin_dem ? String(item.hora_fin_dem).substring(0,5) : '',
      dt_prev_dem: item.dt_prev_dem ? String(item.dt_prev_dem).substring(0,10) : '',
      dt_prev_dem_seg: item.dt_prev_dem_seg ? String(item.dt_prev_dem_seg).substring(0,10) : '',
      status: item.status || 'Backlog',
      pri_dem: item.pri_dem || 'Normal',
      obs_dem: item.obs_dem || ''
    });
  };

  const closeEditModal = () => {
    setEditModalItem(null);
    setEditModalDraft(null);
  };

  const saveEditModal = async () => {
    if (!editModalItem || !editModalDraft) return;
    await onUpdateQuick(editModalItem, editModalDraft);
    closeEditModal();
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
      <div className="flex items-center justify-end gap-2 p-2 border-b">
        <IconButton
          icon={LayoutDashboard}
          onClick={() => setDashboardView(v => !v)}
          variant="secondary"
          size="sm"
          title={dashboardView ? 'Sair da Visão Dashboard' : 'Visão Dashboard'}
        />
        {!allEdit ? (
          <IconButton icon={Edit3} onClick={toggleAllEdit} variant="warning" size="sm" title="Editar tudo" />
        ) : (
          <>
            <IconButton icon={Save} onClick={saveAll} variant="primary" size="sm" title="Salvar tudo" />
            <IconButton icon={X} onClick={cancelAll} variant="secondary" size="sm" title="Cancelar" />
          </>
        )}
      </div>
      {dashboardView && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
          <div className="flex items-center justify-between px-4 md:px-6 py-2 border-b">
            <div className="text-sm font-medium">Visão Dashboard</div>
            <IconButton
              icon={X}
              onClick={() => setDashboardView(false)}
              variant="secondary"
              size="sm"
              title="Fechar"
            />
          </div>
          <div className="flex-1 overflow-auto px-4 md:px-6 py-3">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="sticky top-0 z-10 bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort('descr_dem')}>Título (Demanda) {sortKey==='descr_dem' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort('respon_dem')}>Responsável {sortKey==='respon_dem' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort('dt_prev_dem')}>Data Prevista {sortKey==='dt_prev_dem' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort('dt_prev_dem_seg')}>Data Reagendada {sortKey==='dt_prev_dem_seg' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort('pri_dem')}>Prioridade {sortKey==='pri_dem' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {loading && (
                  <tr>
                    <td className="px-3 py-2 text-xs text-gray-500" colSpan={5}>Carregando...</td>
                  </tr>
                )}
                {!loading && dashboardItems.length === 0 && (
                  <tr>
                    <td className="px-3 py-2 text-xs text-gray-500" colSpan={5}>Nenhuma demanda encontrada</td>
                  </tr>
                )}
                {dashboardItems.map(item => (
                  <tr key={item.id_dem} className={`hover:bg-gray-50 ${getRowClass(item)}`}>
                    <td className="px-3 py-2 text-xs text-gray-900">{item.descr_dem || ''}</td>
                    <td className="px-3 py-2 text-xs text-gray-900">{getPersonName(item.respon_dem)}</td>
                    <td className="px-3 py-2 text-xs text-gray-900">{formatDate(item.dt_prev_dem)}</td>
                    <td className="px-3 py-2 text-xs text-gray-900">{formatDate(item.dt_prev_dem_seg)}</td>
                    <td className="px-3 py-2 text-xs text-gray-900">{item.pri_dem || 'Normal'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!dashboardView && (
      <div className="overflow-y-auto max-h-[70vh]">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="sticky top-0 z-10 bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort('id_dem')}>ID {sortKey==='id_dem' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort('descr_dem')}>Título (Demanda) {sortKey==='descr_dem' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 cursor-pointer relative" onClick={() => toggleSort('respon_dem')}>
              <div className="flex items-center gap-2">
                <span>Responsável {sortKey==='respon_dem' ? (sortDir==='asc'?'▲':'▼') : ''}</span>
                <IconButton
                  icon={Filter}
                  size="sm"
                  variant={isActiveFilter(respFilterSelected, availableResponsibles) ? 'primary' : 'secondary'}
                  title="Filtrar por responsável"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRespFilterOpen(o => !o);
                    setRespFilterSearch('');
                    setRespFilterTempSelected(respFilterSelected === null ? availableResponsibles : respFilterSelected);
                    const rect = e.currentTarget.getBoundingClientRect();
                    setRespFilterAnchor({ top: rect.bottom + 4 + window.scrollY, left: rect.left + window.scrollX });
                  }}
                />
              </div>

              {respFilterOpen && (
                <div className="fixed z-50 w-64 bg-white border border-gray-200 rounded shadow" style={{ top: respFilterAnchor?.top, left: respFilterAnchor?.left }}>
                  <div className="p-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs text-gray-600">Mostrando {availableResponsibles.length}</div>
                      <div className="text-xs">
                        <IconButton
                          icon={CheckSquare}
                          size="sm"
                          variant="secondary"
                          title="Selecionar tudo"
                          onClick={() => setRespFilterTempSelected(availableResponsibles)}
                          className="mr-2"
                        />
                        <IconButton
                          icon={Trash2}
                          size="sm"
                          variant="secondary"
                          title="Limpar"
                          onClick={() => setRespFilterTempSelected([])}
                        />
                      </div>
                    </div>
                    <div className="mb-2">
                      <input
                        type="text"
                        value={respFilterSearch}
                        onChange={(e) => setRespFilterSearch(e.target.value)}
                        placeholder="Pesquisar..."
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                      />
                    </div>
                    <div className="max-h-40 overflow-auto border border-gray-100 rounded">
                      {availableResponsibles
                        .filter(r => {
                          const label = r === '' ? '(Espaços em branco)' : r;
                          return label.toLowerCase().includes(respFilterSearch.toLowerCase());
                        })
                        .map(r => {
                          const label = r === '' ? '(Espaços em branco)' : r;
                          const checked = respFilterTempSelected.includes(r);
                          return (
                            <label key={label} className="flex items-center gap-2 px-2 py-1 text-xs cursor-pointer">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  setRespFilterTempSelected(prev => {
                                    if (isChecked) {
                                      if (prev.includes(r)) return prev;
                                      return [...prev, r];
                                    } else {
                                      return prev.filter(v => v !== r);
                                    }
                                  });
                                }}
                              />
                              <span>{label}</span>
                            </label>
                          );
                        })}
                    </div>
                    <div className="flex justify-end gap-2 mt-2">
                      <IconButton
                        icon={X}
                        size="sm"
                        variant="secondary"
                        title="Cancelar"
                        onClick={() => {
                          setRespFilterOpen(false);
                          setRespFilterSearch('');
                        }}
                      />
                      <IconButton
                        icon={Check}
                        size="sm"
                        variant="success"
                        title="Aplicar filtro"
                        onClick={() => {
                          setRespFilterSelected(respFilterTempSelected);
                          setRespFilterOpen(false);
                          setRespFilterSearch('');
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 cursor-pointer relative" onClick={() => toggleSort('solic_dem')}>
              <div className="flex items-center gap-2">
                <span>Solicitante {sortKey==='solic_dem' ? (sortDir==='asc'?'▲':'▼') : ''}</span>
                <IconButton
                  icon={Filter}
                  size="sm"
                  variant={isActiveFilter(solFilterSelected, availableRequesters) ? 'primary' : 'secondary'}
                  title="Filtrar por solicitante"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSolFilterOpen(o => !o);
                    setSolFilterSearch('');
                    setSolFilterTempSelected(solFilterSelected === null ? availableRequesters : solFilterSelected);
                    const rect = e.currentTarget.getBoundingClientRect();
                    setSolFilterAnchor({ top: rect.bottom + 4 + window.scrollY, left: rect.left + window.scrollX });
                  }}
                />
              </div>

              {solFilterOpen && (
                <div className="fixed z-50 w-64 bg-white border border-gray-200 rounded shadow" style={{ top: solFilterAnchor?.top, left: solFilterAnchor?.left }}>
                  <div className="p-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs text-gray-600">Mostrando {availableRequesters.length}</div>
                      <div className="text-xs">
                        <IconButton
                          icon={CheckSquare}
                          size="sm"
                          variant="secondary"
                          title="Selecionar tudo"
                          onClick={() => setSolFilterTempSelected(availableRequesters)}
                          className="mr-2"
                        />
                        <IconButton
                          icon={Trash2}
                          size="sm"
                          variant="secondary"
                          title="Limpar"
                          onClick={() => setSolFilterTempSelected([])}
                        />
                      </div>
                    </div>
                    <div className="mb-2">
                      <input
                        type="text"
                        value={solFilterSearch}
                        onChange={(e) => setSolFilterSearch(e.target.value)}
                        placeholder="Pesquisar..."
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                      />
                    </div>
                    <div className="max-h-40 overflow-auto border border-gray-100 rounded">
                      {availableRequesters
                        .filter(s => {
                          const label = s === '' ? '(Espaços em branco)' : s;
                          return label.toLowerCase().includes(solFilterSearch.toLowerCase());
                        })
                        .map(s => {
                          const label = s === '' ? '(Espaços em branco)' : s;
                          const checked = solFilterTempSelected.includes(s);
                          return (
                            <label key={label} className="flex items-center gap-2 px-2 py-1 text-xs cursor-pointer">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  setSolFilterTempSelected(prev => {
                                    if (isChecked) {
                                      if (prev.includes(s)) return prev;
                                      return [...prev, s];
                                    } else {
                                      return prev.filter(v => v !== s);
                                    }
                                  });
                                }}
                              />
                              <span>{label}</span>
                            </label>
                          );
                        })}
                    </div>
                    <div className="flex justify-end gap-2 mt-2">
                      <IconButton
                        icon={X}
                        size="sm"
                        variant="secondary"
                        title="Cancelar"
                        onClick={() => {
                          setSolFilterOpen(false);
                          setSolFilterSearch('');
                        }}
                      />
                      <IconButton
                        icon={Check}
                        size="sm"
                        variant="success"
                        title="Aplicar filtro"
                        onClick={() => {
                          setSolFilterSelected(solFilterTempSelected);
                          setSolFilterOpen(false);
                          setSolFilterSearch('');
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort('dt_cad_dem')}>Data Cadastro {sortKey==='dt_cad_dem' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort('dt_cad_ini')}>Data Início {sortKey==='dt_cad_ini' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Hora (Início)</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Hora (Final)</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort('dt_prev_dem')}>Data Prevista {sortKey==='dt_prev_dem' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort('dt_prev_dem_seg')}>Data Reagendada {sortKey==='dt_prev_dem_seg' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 cursor-pointer" onClick={() => toggleSort('dt_concl_dem')}>Data Conclusão {sortKey==='dt_concl_dem' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 cursor-pointer relative" onClick={() => toggleSort('status')}>
              <div className="flex items-center gap-2">
                <span>Status {sortKey==='status' ? (sortDir==='asc'?'▲':'▼') : ''}</span>
                <IconButton
                  icon={Filter}
                  size="sm"
                  variant={isActiveFilter(statusFilterSelected, availableStatuses) ? 'primary' : 'secondary'}
                  title="Filtrar por status"
                  onClick={(e) => {
                    e.stopPropagation();
                    setStatusFilterOpen(o => !o);
                    setStatusFilterSearch('');
                    setStatusFilterTempSelected(statusFilterSelected === null ? availableStatuses : statusFilterSelected);
                    const rect = e.currentTarget.getBoundingClientRect();
                    setStatusFilterAnchor({ top: rect.bottom + 4 + window.scrollY, left: rect.left + window.scrollX });
                  }}
                />
              </div>

              {statusFilterOpen && (
                <div className="fixed z-50 w-64 bg-white border border-gray-200 rounded shadow" style={{ top: statusFilterAnchor?.top, left: statusFilterAnchor?.left }}>
                  <div className="p-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs text-gray-600">Mostrando {availableStatuses.length}</div>
                      <div className="text-xs">
                        <IconButton
                          icon={CheckSquare}
                          size="sm"
                          variant="secondary"
                          title="Selecionar tudo"
                          onClick={() => setStatusFilterTempSelected(availableStatuses)}
                          className="mr-2"
                        />
                        <IconButton
                          icon={Trash2}
                          size="sm"
                          variant="secondary"
                          title="Limpar"
                          onClick={() => setStatusFilterTempSelected([])}
                        />
                      </div>
                    </div>
                    <div className="mb-2">
                      <input
                        type="text"
                        value={statusFilterSearch}
                        onChange={(e) => setStatusFilterSearch(e.target.value)}
                        placeholder="Pesquisar..."
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                      />
                    </div>
                    <div className="max-h-40 overflow-auto border border-gray-100 rounded">
                      {availableStatuses
                        .filter(s => {
                          const label = s === '' ? '(Espaços em branco)' : s;
                          return label.toLowerCase().includes(statusFilterSearch.toLowerCase());
                        })
                        .map(s => {
                          const label = s === '' ? '(Espaços em branco)' : s;
                          const checked = statusFilterTempSelected.includes(s);
                          return (
                            <label key={label} className="flex items-center gap-2 px-2 py-1 text-xs cursor-pointer">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  setStatusFilterTempSelected(prev => {
                                    if (isChecked) {
                                      if (prev.includes(s)) return prev;
                                      return [...prev, s];
                                    } else {
                                      return prev.filter(v => v !== s);
                                    }
                                  });
                                }}
                              />
                              <span>{label}</span>
                            </label>
                          );
                        })}
                    </div>
                    <div className="flex justify-end gap-2 mt-2">
                      <IconButton
                        icon={X}
                        size="sm"
                        variant="secondary"
                        title="Cancelar"
                        onClick={() => {
                          setStatusFilterOpen(false);
                          setStatusFilterSearch('');
                        }}
                      />
                      <IconButton
                        icon={Check}
                        size="sm"
                        variant="success"
                        title="Aplicar filtro"
                        onClick={() => {
                          setStatusFilterSelected(statusFilterTempSelected);
                          setStatusFilterOpen(false);
                          setStatusFilterSearch('');
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 cursor-pointer relative" onClick={() => toggleSort('pri_dem')}>
              <div className="flex items-center gap-2">
                <span>Prioridade {sortKey==='pri_dem' ? (sortDir==='asc'?'▲':'▼') : ''}</span>
                <IconButton
                  icon={Filter}
                  size="sm"
                  variant={isActiveFilter(prioFilterSelected, availablePriorities) ? 'primary' : 'secondary'}
                  title="Filtrar por prioridade"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPrioFilterOpen(o => !o);
                    setPrioFilterSearch('');
                    setPrioFilterTempSelected(prioFilterSelected === null ? availablePriorities : prioFilterSelected);
                    const rect = e.currentTarget.getBoundingClientRect();
                    setPrioFilterAnchor({ top: rect.bottom + 4 + window.scrollY, left: rect.left + window.scrollX });
                  }}
                />
              </div>

              {prioFilterOpen && (
                <div className="fixed z-50 w-64 bg-white border border-gray-200 rounded shadow" style={{ top: prioFilterAnchor?.top, left: prioFilterAnchor?.left }}>
                  <div className="p-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs text-gray-600">Mostrando {availablePriorities.length}</div>
                      <div className="text-xs">
                        <IconButton
                          icon={CheckSquare}
                          size="sm"
                          variant="secondary"
                          title="Selecionar tudo"
                          onClick={() => setPrioFilterTempSelected(availablePriorities)}
                          className="mr-2"
                        />
                        <IconButton
                          icon={Trash2}
                          size="sm"
                          variant="secondary"
                          title="Limpar"
                          onClick={() => setPrioFilterTempSelected([])}
                        />
                      </div>
                    </div>
                    <div className="mb-2">
                      <input
                        type="text"
                        value={prioFilterSearch}
                        onChange={(e) => setPrioFilterSearch(e.target.value)}
                        placeholder="Pesquisar..."
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                      />
                    </div>
                    <div className="max-h-40 overflow-auto border border-gray-100 rounded">
                      {availablePriorities
                        .filter(p => {
                          const label = p === '' ? '(Espaços em branco)' : p;
                          return label.toLowerCase().includes(prioFilterSearch.toLowerCase());
                        })
                        .map(p => {
                          const label = p === '' ? '(Espaços em branco)' : p;
                          const checked = prioFilterTempSelected.includes(p);
                          return (
                            <label key={label} className="flex items-center gap-2 px-2 py-1 text-xs cursor-pointer">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  setPrioFilterTempSelected(prev => {
                                    if (isChecked) {
                                      if (prev.includes(p)) return prev;
                                      return [...prev, p];
                                    } else {
                                      return prev.filter(v => v !== p);
                                    }
                                  });
                                }}
                              />
                              <span>{label}</span>
                            </label>
                          );
                        })}
                    </div>
                    <div className="flex justify-end gap-2 mt-2">
                      <IconButton
                        icon={X}
                        size="sm"
                        variant="secondary"
                        title="Cancelar"
                        onClick={() => {
                          setPrioFilterOpen(false);
                          setPrioFilterSearch('');
                        }}
                      />
                      <IconButton
                        icon={Check}
                        size="sm"
                        variant="success"
                        title="Aplicar filtro"
                        onClick={() => {
                          setPrioFilterSelected(prioFilterTempSelected);
                          setPrioFilterOpen(false);
                          setPrioFilterSearch('');
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Obs.</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Ações</th>
          </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
          {loading && (
            <tr>
              <td className="px-3 py-2 text-xs text-gray-500" colSpan={15}>Carregando...</td>
            </tr>
          )}
          {!loading && sorted.length === 0 && (
            <tr>
              <td className="px-3 py-2 text-xs text-gray-500" colSpan={15}>Nenhuma demanda encontrada</td>
            </tr>
          )}
          {sorted.map(item => (
            <tr key={item.id_dem} className={`hover:bg-gray-50 ${getRowClass(item)}`}>
              <td className="px-3 py-2 text-xs text-gray-900">{item.id_dem}</td>
              <td className="px-3 py-2 text-xs text-gray-900">
                {editing[item.id_dem] ? (
                  <input
                    value={editing[item.id_dem].descr_dem}
                    onChange={(e) => setEditing(ed => ({ ...ed, [item.id_dem]: { ...ed[item.id_dem], descr_dem: e.target.value } }))}
                    className="w-full px-2 py-1 border border-gray-300 rounded"
                    placeholder="Descrição"
                  />
                ) : (
                  <span>{item.descr_dem || ''}</span>
                )}
              </td>
              <td className="px-3 py-2 text-xs text-gray-900">
                {editing[item.id_dem] ? (
                  <select
                    value={editing[item.id_dem].respon_dem}
                    onChange={(e) => setEditing(ed => ({ ...ed, [item.id_dem]: { ...ed[item.id_dem], respon_dem: e.target.value } }))}
                    className="w-full px-2 py-1 border border-gray-300 rounded"
                  >
                    <option value="">Selecione</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                ) : (
            <span>{getPersonName(item.respon_dem)}</span>
                )}
              </td>
              <td className="px-3 py-2 text-xs text-gray-900">
                {editing[item.id_dem] ? (
                  <select
                    value={editing[item.id_dem].solic_dem}
                    onChange={(e) => setEditing(ed => ({ ...ed, [item.id_dem]: { ...ed[item.id_dem], solic_dem: e.target.value } }))}
                    className="w-full px-2 py-1 border border-gray-300 rounded"
                  >
                    <option value="">Selecione</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                ) : (
            <span>{getPersonName(item.solic_dem)}</span>
                )}
              </td>
              <td className="px-3 py-2 text-xs text-gray-700">{formatDate(item.dt_cad_dem)}</td>
              <td className="px-3 py-2 text-xs text-gray-900">
                {editing[item.id_dem] ? (
                  <input
                    type="date"
                    value={editing[item.id_dem].dt_cad_ini}
                    onChange={(e) => setEditing(ed => ({ ...ed, [item.id_dem]: { ...ed[item.id_dem], dt_cad_ini: e.target.value } }))}
                    className="px-2 py-1 border border-gray-300 rounded"
                  />
                ) : (
                  <span>{formatDate(item.dt_cad_ini)}</span>
                )}
              </td>
              <td className="px-3 py-2 text-xs text-gray-900">
                {editing[item.id_dem] ? (
                  <input
                    type="time"
                    value={editing[item.id_dem].hora_ini_dem}
                    onChange={(e) => setEditing(ed => ({ ...ed, [item.id_dem]: { ...ed[item.id_dem], hora_ini_dem: e.target.value } }))}
                    className="px-2 py-1 border border-gray-300 rounded"
                  />
                ) : (
                  <span>{item.hora_ini_dem || '—'}</span>
                )}
              </td>
              <td className="px-3 py-2 text-xs text-gray-900">
                {editing[item.id_dem] ? (
                  <input
                    type="time"
                    value={editing[item.id_dem].hora_fin_dem}
                    onChange={(e) => setEditing(ed => ({ ...ed, [item.id_dem]: { ...ed[item.id_dem], hora_fin_dem: e.target.value } }))}
                    className="px-2 py-1 border border-gray-300 rounded"
                  />
                ) : (
                  <span>{item.hora_fin_dem || '—'}</span>
                )}
              </td>
              <td className="px-3 py-2 text-xs text-gray-900">
                {/* Data Prevista bloqueada para edição após cadastro */}
                <span>{formatDate(item.dt_prev_dem)}</span>
              </td>
              <td className="px-3 py-2 text-xs text-gray-900">
                {editing[item.id_dem] ? (
                  <input
                    type="date"
                    value={editing[item.id_dem].dt_prev_dem_seg}
                    onChange={(e) => setEditing(ed => ({ ...ed, [item.id_dem]: { ...ed[item.id_dem], dt_prev_dem_seg: e.target.value } }))}
                    className="px-2 py-1 border border-gray-300 rounded"
                  />
                ) : (
                  <span>{formatDate(item.dt_prev_dem_seg)}</span>
                )}
              </td>
              <td className="px-3 py-2 text-xs text-gray-700">{formatDate(item.dt_concl_dem)}</td>
              <td className="px-3 py-2">
                {editing[item.id_dem] ? (
                  <select
                    value={editing[item.id_dem].status}
                    onChange={(e) => setEditing(ed => ({ ...ed, [item.id_dem]: { ...ed[item.id_dem], status: e.target.value } }))}
                    className="text-xs px-2 py-1 border border-gray-300 rounded"
                  >
                    {STATUS_COLUMNS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                ) : (
                  <span className="text-xs">{item.status || 'Backlog'}</span>
                )}
              </td>
              <td className="px-3 py-2">
                {editing[item.id_dem] ? (
                  <select
                    value={editing[item.id_dem].pri_dem}
                    onChange={(e) => setEditing(ed => ({ ...ed, [item.id_dem]: { ...ed[item.id_dem], pri_dem: e.target.value } }))}
                    className="text-xs px-2 py-1 border border-gray-300 rounded"
                  >
                    {PRIORIDADES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                ) : (
                  <span className="text-xs">{item.pri_dem || 'Normal'}</span>
                )}
              </td>
              <td className="px-3 py-2">
                <IconButton
                  icon={MessageSquare}
                  onClick={() => setObsModalItem(item)}
                  variant="info"
                  size="sm"
                  title={item.obs_dem && String(item.obs_dem).trim() ? 'Ver observação' : 'Sem observação'}
                />
              </td>
              <td className="px-3 py-2">
                {isCompleted(item) ? (
                  <button className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded cursor-not-allowed" disabled>Concluído</button>
                ) : editing[item.id_dem] ? (
                  <div className="flex gap-2">
                    <IconButton icon={Save} onClick={() => saveEdit(item)} variant="primary" size="sm" title="Salvar" />
                    <IconButton icon={X} onClick={() => cancelEdit(item.id_dem)} variant="secondary" size="sm" title="Cancelar" />
                  </div>
                ) : (
                  <IconButton
                    icon={Edit3}
                    onClick={() => openEditModal(item)}
                    variant="warning"
                    size="sm"
                    title="Editar"
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      )}

      {/* Modal de Observação */}
      {obsModalItem && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => setObsModalItem(null)}>
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Observação da Demanda #{obsModalItem.id_dem}</h3>
              <div className="p-3 border border-gray-200 rounded bg-gray-50 whitespace-pre-wrap text-sm text-gray-800">
                {obsModalItem.obs_dem && String(obsModalItem.obs_dem).trim() ? obsModalItem.obs_dem : 'Sem observação.'}
              </div>
              <div className="mt-4 flex justify-end">
                <IconButton icon={X} onClick={() => setObsModalItem(null)} variant="secondary" title="Fechar" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição por Linha */}
      {editModalItem && editModalDraft && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={closeEditModal}>
          <div className="relative top-16 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="mt-1">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Editar Demanda #{editModalItem.id_dem}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                  <input
                    type="text"
                    value={editModalDraft.descr_dem}
                    onChange={(e) => setEditModalDraft(d => ({ ...d, descr_dem: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Responsável</label>
                  <select
                    value={editModalDraft.respon_dem}
                    onChange={(e) => setEditModalDraft(d => ({ ...d, respon_dem: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Selecione</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Solicitante</label>
                  <select
                    value={editModalDraft.solic_dem}
                    onChange={(e) => setEditModalDraft(d => ({ ...d, solic_dem: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Selecione</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Início</label>
                  <input
                    type="date"
                    value={editModalDraft.dt_cad_ini}
                    onChange={(e) => setEditModalDraft(d => ({ ...d, dt_cad_ini: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hora Início</label>
                  <input
                    type="time"
                    value={editModalDraft.hora_ini_dem}
                    onChange={(e) => setEditModalDraft(d => ({ ...d, hora_ini_dem: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hora Fim</label>
                  <input
                    type="time"
                    value={editModalDraft.hora_fin_dem}
                    onChange={(e) => setEditModalDraft(d => ({ ...d, hora_fin_dem: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Prevista</label>
                  <input
                    type="date"
                    value={editModalItem?.dt_prev_dem ? String(editModalItem.dt_prev_dem).substring(0,10) : ''}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">Campo bloqueado após cadastro.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Reagendada</label>
                  <input
                    type="date"
                    value={editModalDraft.dt_prev_dem_seg}
                    onChange={(e) => setEditModalDraft(d => ({ ...d, dt_prev_dem_seg: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editModalDraft.status}
                    onChange={(e) => setEditModalDraft(d => ({ ...d, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    {STATUS_COLUMNS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prioridade</label>
                  <select
                    value={editModalDraft.pri_dem}
                    onChange={(e) => setEditModalDraft(d => ({ ...d, pri_dem: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    {PRIORIDADES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observação</label>
                  <textarea
                    rows={4}
                    value={editModalDraft.obs_dem}
                    onChange={(e) => setEditModalDraft(d => ({ ...d, obs_dem: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div className="mt-5 flex items-center justify-end gap-2">
                <IconButton icon={X} onClick={closeEditModal} variant="secondary" title="Cancelar" />
                <IconButton icon={Save} onClick={saveEditModal} variant="primary" title="Salvar" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemandasGrid;